import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, ProfessionalTypePicker } from '../components';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import TagSelector from '../components/TagSelector';
import { useProfileTaxonomy } from '../hooks/useProfileTaxonomy';
import {
  PROFESSIONAL_TYPE_LABELS,
  type ProfessionalType,
} from '../types/professionalRates';

interface SearchCriteria {
  professional_type?: string;
  first_name?: string;
  last_name?: string;
  gender_preference?: string;
  availability_status?: string;
  available_from_date?: string;
  specialisatie_tags?: string[];
  doelgroep_niveaus?: string[];
  active_regions?: string[];
  languages?: string[];
  language_required?: boolean;
  search_by_language_and_type_only?: boolean;
  affinity?: string[];
  active_attitude?: boolean;
  belagom_search_team_enabled?: boolean;
  /** Minimale vetting-tier (GOUD/ZILVER/BRONS) */
  min_tier?: string;
}

const REGIONS = [
  'Noord-Holland', 'Zuid-Holland', 'Utrecht', 'Gelderland', 'Noord-Brabant',
  'Limburg', 'Zeeland', 'Friesland', 'Groningen', 'Drenthe', 'Overijssel', 'Flevoland'
];

const DEFAULT_LANGUAGE = 'Nederlands';
const OTHER_LANGUAGES = [
  'Engels', 'Duits', 'Frans', 'Spaans', 'Italiaans', 'Portugees', 'Pools', 'Turks', 'Arabisch'
];

const INDUSTRIES = [
  'Zorg & Welzijn', 'Onderwijs', 'Overheid', 'Financiële dienstverlening',
  'ICT', 'Productie & Industrie', 'Retail', 'Horeca', 'Transport & Logistiek', 'Bouw'
];

type SearchMode = 'search' | 'vacancy';
type Step = 'type-picker' | 'filters';

export const EmployerProfessionalSearchPage: React.FC = () => {
  const { t } = useTranslation('employer');
  const { t: tProfile } = useTranslation('profile');
  const { t: tDash } = useTranslation('employerDashboard');
  // Specialisme- en doelgroepfilters komen uit dezelfde taxonomie als het profiel
  const { data: taxonomy } = useProfileTaxonomy();
  useCockpitTitle(t('search.title', 'Professioneel Zoeken'), t('search.subtitle', 'Vind de juiste specialist voor uw organisatie'));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchMode, setSearchMode] = useState<SearchMode | null>(null);
  const [step, setStep] = useState<Step>('type-picker');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [criteria, setCriteria] = useState<SearchCriteria>({
    gender_preference: 'no_preference',
    availability_status: 'any',
    active_regions: [],
    languages: [],
    affinity: [],
    search_by_language_and_type_only: false,
  });

  useEffect(() => {
    const state = location.state as any;
    if (state?.criteria) {
      setCriteria(prev => ({
        ...prev,
        ...state.criteria,
        active_regions: state.criteria.active_regions || [],
        languages: state.criteria.languages || [],
        affinity: state.criteria.affinity || [],
      }));
      if (state.returnToSearch) {
        setSearchMode('search');
        setStep('filters');
        setShowAdvancedFilters(true);
      }
    }
  }, [location.state]);

  const handleInputChange = (field: keyof SearchCriteria, value: any) => {
    setCriteria(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiSelect = (field: 'active_regions' | 'languages' | 'affinity', value: string) => {
    setCriteria(prev => {
      const current = prev[field] || [];
      const updated = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
      return { ...prev, [field]: updated };
    });
  };

  const handleTypeSelect = (type: ProfessionalType) => {
    setCriteria(prev => ({ ...prev, professional_type: type }));
    setStep('filters');
  };

  const handleSkipType = () => {
    setCriteria(prev => ({ ...prev, professional_type: undefined }));
    setStep('filters');
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    Object.entries(criteria).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== 'any' && value !== 'no_preference') {
        if (Array.isArray(value)) {
          if (value.length > 0) params.set(key, JSON.stringify(value));
        } else if (typeof value === 'boolean') {
          params.set(key, String(value));
        } else {
          params.set(key, String(value));
        }
      }
    });
    navigate(`/employer/dashboard/zoeken/resultaten?${params.toString()}`);
  };

  const handlePublishVacancy = () => {
    navigate('/opdrachten/nieuw', { state: { criteria } });
  };

  const handleBelagomSearch = async () => {
    if (!criteria.professional_type) {
      toast.error(t('search.selectTypeFirst'));
      return;
    }
    try {
      await api.post('/employer/search-request', {
        professional_type: criteria.professional_type,
        search_criteria: criteria,
        publish_on_community: false,
        active_attitude: criteria.active_attitude || false,
        belagom_search_team_enabled: criteria.belagom_search_team_enabled || false
      });
      toast.success(t('search.belagomSearchCreated'));
      setSearchMode(null);
    } catch (error: any) {
      console.error('Error creating search request:', error);
      toast.error(error?.response?.data?.message || t('search.belagomSearchError'));
    }
  };

  // ── Landing ──────────────────────────────────────────────────────────────────

  if (!searchMode) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <p className="mt-4 text-gray-600 leading-relaxed">
            Welkom op BeLagom, hét centrale platform voor hoogwaardige professionals op het gebied van Learning &amp; Development, Psychosociale Arbeidsbelasting, Welzijn en Trauma. Of u nu op zoek bent naar een L&amp;D-adviseur, vertrouwenspersoon, vitaliteitscoach of trauma-expert; wij maken de juiste match met een gecertificeerde zzp'er eenvoudig en transparant.
          </p>
          <p className="mt-3 text-gray-600">
            Als opdrachtgever bepaalt u zelf hoe u uw ideale professional selecteert. Dit kan op drie verschillende manieren:
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <button
            onClick={() => { setSearchMode('search'); setStep('type-picker'); }}
            className="p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
          >
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4">
              <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">Gericht zoeken en direct contact leggen</h3>
            <p className="text-sm text-gray-600">Gebruik uitgebreide zoekfilters op expertise, certificeringen en beschikbaarheid. Gevonden? Direct rechtstreeks contact opnemen.</p>
          </button>
          <button
            onClick={() => { setSearchMode('vacancy'); setStep('type-picker'); }}
            className="p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
          >
            <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">Een vacature of opdracht plaatsen</h3>
            <p className="text-sm text-gray-600">Plaats uw opdracht met gewenst profiel, regio en details. Geïnteresseerde zzp'ers reageren direct, u maakt de selectie.</p>
          </button>
          <button
            onClick={() => navigate('/employer/dashboard/zoeken/kaart')}
            className="p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
          >
            <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mb-4">
              <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">Zoeken via de interactieve kaart</h3>
            <p className="text-sm text-gray-600">Bekijk op de kaart van Nederland welke professionals bij u in de buurt actief zijn, inclusief beschikbaarheid en ervaringsniveau per regio.</p>
          </button>
        </div>
      </div>
    );
  }

  // ── Step 1: Type picker ──────────────────────────────────────────────────────

  if (step === 'type-picker') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">Stap 1 van 2</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Welk type professional zoekt u?</h1>
            <p className="mt-1 text-gray-600 text-sm">
              {searchMode === 'search'
                ? 'Selecteer een type of zoek op naam. U kunt ook doorgaan zonder selectie.'
                : 'Selecteer een type professional voor uw vacature. U kunt ook doorgaan zonder selectie.'}
            </p>
          </div>
          <Button variant="outline" onClick={() => setSearchMode(null)}>
            {t('search.back')}
          </Button>
        </div>


        <Card>
          <ProfessionalTypePicker
            onSelect={handleTypeSelect}
            onSkip={handleSkipType}
            selectedType={criteria.professional_type as ProfessionalType | undefined}
          />
        </Card>
      </div>
    );
  }

  // ── Step 2: Filters ──────────────────────────────────────────────────────────

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">Stap 2 van 2</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{t('search.criteriaTitle')}</h1>
          <p className="mt-1 text-gray-600 text-sm">
            {searchMode === 'search' ? t('search.criteriaSubtitleSearch') : t('search.criteriaSubtitleVacancy')}
          </p>
        </div>
        <Button variant="outline" onClick={() => setSearchMode(null)}>
          {t('search.back')}
        </Button>
      </div>

      <Card>
        <div className="space-y-6">
          {/* Selected type pill */}
          <div className="flex items-center gap-3 pb-4 border-b">
            <span className="text-sm font-medium text-gray-700">Type professional:</span>
            {criteria.professional_type ? (
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full border border-blue-200">
                  {PROFESSIONAL_TYPE_LABELS[criteria.professional_type as ProfessionalType] ?? criteria.professional_type}
                </span>
                <button
                  onClick={() => setStep('type-picker')}
                  className="text-xs text-gray-500 hover:text-blue-600 underline"
                >
                  Wijzigen
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400 italic">Geen type geselecteerd</span>
                <button
                  onClick={() => setStep('type-picker')}
                  className="text-xs text-gray-500 hover:text-blue-600 underline"
                >
                  Selecteren
                </button>
              </div>
            )}
          </div>



          {/* Show certified note when type selected */}
          {criteria.professional_type && (
            <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
              {t('search.certifiedNote')}
            </p>
          )}

          {/* Expand Filters */}
          <div className="pt-4 border-t">
            <button
              type="button"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <span>{showAdvancedFilters ? t('search.hideFilters') : t('search.showFilters')}</span>
              <svg className={`w-5 h-5 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {showAdvancedFilters && (
            <div className="space-y-6 pt-4 border-t">
              {/* Gender Preference */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('search.genderPreference')}</label>
                <div className="flex gap-4">
                  {['no_preference', 'male', 'female'].map(gender => (
                    <label key={gender} className="flex items-center">
                      <input
                        type="radio"
                        name="gender"
                        value={gender}
                        checked={criteria.gender_preference === gender}
                        onChange={(e) => handleInputChange('gender_preference', e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">
                        {gender === 'no_preference' ? t('search.genderNoPreference') : gender === 'male' ? t('search.genderMale') : t('search.genderFemale')}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('search.availability')}</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={criteria.availability_status || 'any'}
                    onChange={(e) => handleInputChange('availability_status', e.target.value)}
                  >
                    <option value="any">{t('search.availabilityAll')}</option>
                    <option value="immediately">{t('search.availabilityImmediate')}</option>
                    <option value="specific_date">{t('search.availabilitySpecificDate')}</option>
                  </select>
                </div>
                {criteria.availability_status === 'specific_date' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('search.availableFrom')}</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={criteria.available_from_date || ''}
                      onChange={(e) => handleInputChange('available_from_date', e.target.value)}
                    />
                  </div>
                )}
              </div>

              {/* Specialisme-tags (FO stap 4) — vervangt het werkwijze-filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {tProfile('label.specialisations').replace(' *', '')}
                </label>
                <TagSelector
                  options={taxonomy?.specialisatie_tags || []}
                  value={criteria.specialisatie_tags || []}
                  onChange={(value) => handleInputChange('specialisatie_tags', value)}
                  placeholder={tProfile('placeholder.selectSpecialisation')}
                />
              </div>

              {/* Doelgroep-niveaus (FO stap 4) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {tProfile('label.targetGroups').replace(' *', '')}
                </label>
                <TagSelector
                  options={taxonomy?.doelgroep_niveaus || []}
                  value={criteria.doelgroep_niveaus || []}
                  onChange={(value) => handleInputChange('doelgroep_niveaus', value)}
                  variant="checkbox"
                />
              </div>

              {/* Vetting-tier */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{tDash('zoeken.criteria.tier')}</label>
                <select
                  value={criteria.min_tier || ''}
                  onChange={(e) => handleInputChange('min_tier', e.target.value || undefined)}
                  className="w-full md:w-80 rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {['', 'BRONS', 'ZILVER', 'GOUD'].map((w) => (
                    <option key={w} value={w}>{tDash(`zoeken.tierOptie.${w || 'ALLE'}`)}</option>
                  ))}
                </select>
              </div>

              {/* Regions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('search.regions')}</label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {REGIONS.map(region => (
                    <label key={region} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={criteria.active_regions?.includes(region) || false}
                        onChange={() => handleMultiSelect('active_regions', region)}
                        className="mr-2"
                      />
                      <span className="text-sm">{region}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('search.languages')}
                </label>
                <p className="text-xs text-gray-500 mb-3">{t('search.languagesDesc')}</p>
                <div className="space-y-3">
                  <div className="flex items-center p-2 bg-blue-50 rounded border border-blue-200">
                    <input type="checkbox" checked={true} disabled={true} className="mr-2" />
                    <span className="text-sm font-medium text-blue-900">{DEFAULT_LANGUAGE} ({t('search.default')})</span>
                  </div>
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-gray-700">{t('search.otherLanguages')}:</span>
                      <button
                        type="button"
                        onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center border border-blue-300 rounded px-2 py-1 hover:bg-blue-50"
                      >
                        + {t('search.addLanguage')}
                      </button>
                    </div>
                    {showLanguageDropdown && (
                      <div className="absolute z-10 mt-1 w-48 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {OTHER_LANGUAGES.filter(lang => !criteria.languages?.includes(lang)).map(lang => (
                          <button
                            key={lang}
                            type="button"
                            onClick={() => { handleMultiSelect('languages', lang); setShowLanguageDropdown(false); }}
                            className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm"
                          >
                            {lang}
                          </button>
                        ))}
                        {OTHER_LANGUAGES.filter(lang => !criteria.languages?.includes(lang)).length === 0 && (
                          <div className="px-4 py-2 text-sm text-gray-500">{t('search.allLanguagesAdded')}</div>
                        )}
                      </div>
                    )}
                    {criteria.languages && criteria.languages.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {criteria.languages.map(lang => (
                          <div key={lang} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded border">
                            <span className="text-sm">{lang}</span>
                            <button type="button" onClick={() => handleMultiSelect('languages', lang)} className="text-red-600 hover:text-red-700 text-xs ml-1">×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {criteria.languages && criteria.languages.length > 0 && (
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={criteria.language_required || false}
                          onChange={(e) => handleInputChange('language_required', e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{t('search.languageRequired')}</span>
                      </label>
                      <p className="text-xs text-gray-500 ml-6">{t('search.languageRequiredHint')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Affiniteit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('search.affinity')}</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {INDUSTRIES.map(industry => (
                    <label key={industry} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={criteria.affinity?.includes(industry) || false}
                        onChange={() => handleMultiSelect('affinity', industry)}
                        className="mr-2"
                      />
                      <span className="text-sm">{industry}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Actieve houding */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={criteria.active_attitude || false}
                    onChange={(e) => handleInputChange('active_attitude', e.target.checked)}
                    className="mr-2 mt-0.5"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Graag actieve houding</span>
                    <p className="text-xs text-gray-500 mt-0.5">Professional neemt proactief initiatief in communicatie en aanpak</p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Belagom Search Team */}
          {searchMode === 'vacancy' && (
            <div className="pt-4 border-t">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={criteria.belagom_search_team_enabled || false}
                  onChange={(e) => handleInputChange('belagom_search_team_enabled', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">{t('search.belagomSearchTeam')}</span>
              </label>
              <p className="mt-1 text-xs text-gray-500 ml-6">{t('search.belagomSearchTeamDesc')}</p>
            </div>
          )}

          {/* Actions */}
          <div className="pt-6 border-t flex justify-between items-center gap-3">
            <button
              type="button"
              onClick={() => setStep('type-picker')}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              ← Type wijzigen
            </button>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setSearchMode(null)}>
                {t('search.cancel')}
              </Button>
              {searchMode === 'search' && (
                <>
                  <Button variant="outline" onClick={handleBelagomSearch}>
                    {t('search.belagomSearch')}
                  </Button>
                  <Button onClick={handleSearch}>
                    {t('search.searchDatabase')}
                  </Button>
                </>
              )}
              {searchMode === 'vacancy' && (
                <Button onClick={handlePublishVacancy}>
                  {t('search.continueToVacancy')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
